import React from 'react';
import { Link } from 'react-router-dom';
import { openConsentPreferences, withdrawAllConsent } from '../utils/consentManager';

const PrivacyPolicy: React.FC = () => {
  const handleOpenPreferences = () => {
    if (typeof window !== 'undefined') {
      openConsentPreferences();
    }
  };

  const handleWithdrawConsent = () => {
    if (typeof window !== 'undefined') {
      withdrawAllConsent();
    }
  };

  const handleOpenInteractiveNotice = () => {
    if (typeof window !== 'undefined') {
      const modal = document.getElementById('syra-notice-modal');
      if (modal) {
        modal.classList.add('active');
        return;
      }
      const btn = document.getElementById('syra-notice-btn');
      if (btn) {
        btn.click();
      }
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8 font-poppins">
      <div className="max-w-5xl mx-auto">
        {/* Breadcrumb Navigation */}
        <nav className="mb-6 text-sm text-gray-500 flex items-center space-x-2" aria-label="Breadcrumb">
          <Link to="/" className="text-primary hover:underline">Home</Link>
          <span>/</span>
          <span className="text-gray-800 font-medium">Privacy Policy &amp; DPDPA Notice</span>
        </nav>

        {/* Header Hero Card */}
        <div className="bg-gradient-to-r from-primary to-primary-dark rounded-2xl shadow-xl p-8 sm:p-10 text-white mb-8">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-200 border border-emerald-400/30">
              <span className="w-2 h-2 rounded-full bg-emerald-400 mr-2 animate-pulse"></span>
              DPDP Act, 2023 Compliant (Sections 5, 6, 8, 10, 11, 12, 14 &amp; Rules 3, 14, 19)
            </span>
            <div className="text-xs sm:text-sm text-white/80 space-x-3">
              <span><strong>Version:</strong> 2.1</span>
              <span>•</span>
              <span><strong>Last Updated:</strong> September 29, 2026</span>
              <span>•</span>
              <span><strong>Notice ID:</strong> <code className="bg-white/10 px-2 py-0.5 rounded text-white font-mono text-xs">6439c25a-00d9-416a-b622-16bdfb4730ef</code></span>
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Privacy Policy &amp; Digital Personal Data Notice
          </h1>
          <p className="text-white/90 text-sm sm:text-base leading-relaxed max-w-3xl">
            Lakshmi Venkatesan Charitable Trust (&quot;LV Trust&quot;, &quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) is committed to protecting your privacy and personal data in strict accordance with India&apos;s <strong>Digital Personal Data Protection Act, 2023 (DPDP Act)</strong> and the rules framed thereunder.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleOpenPreferences}
              className="inline-flex items-center px-4 py-2.5 rounded-lg bg-secondary hover:bg-secondary-light text-white font-semibold text-xs sm:text-sm shadow-md transition-all active:scale-95"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              Manage Privacy Preferences
            </button>
            <button
              type="button"
              onClick={handleWithdrawConsent}
              className="inline-flex items-center px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold text-xs sm:text-sm shadow-md transition-all active:scale-95"
              title="Withdraw all optional consent as easily as it was given (§6(4))"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Withdraw Consent (§6(4))
            </button>
            <a
              href="#your-rights"
              className="inline-flex items-center px-4 py-2.5 rounded-lg bg-white/15 hover:bg-white/25 text-white border border-white/20 font-semibold text-xs sm:text-sm transition-all"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Your Rights (§11–§14)
            </a>
            <a
              href="#sdf-applicability"
              className="inline-flex items-center px-4 py-2.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-400/30 font-semibold text-xs sm:text-sm transition-all"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              SDF Status (§10)
            </a>
            <button
              type="button"
              onClick={handleOpenInteractiveNotice}
              className="inline-flex items-center px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white border border-white/20 font-semibold text-xs sm:text-sm transition-all"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Interactive DPDPA Widget
            </button>
          </div>
        </div>

        {/* Section 1: Plain-Language Layered Summary (PNA-02) */}
        <section className="bg-amber-50/70 border-l-4 border-secondary rounded-xl p-6 mb-8 shadow-sm">
          <div className="flex items-center space-x-2 text-secondary-dark font-bold text-lg mb-2">
            <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2>1. Plain-Language Summary (Rule 3 Intelligibility Requirement)</h2>
          </div>
          <p className="text-gray-700 text-sm leading-relaxed mb-3">
            We believe you should understand how your personal data is handled without wading through legal jargon. Here is a quick, plain-language summary of our data practices:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs sm:text-sm text-gray-700">
            <div className="bg-white p-3.5 rounded-lg border border-amber-200">
              <strong className="text-gray-900 block mb-1">What we collect:</strong>
              Contact information (name, email, phone, address), demographic info (age, gender), statutory tax ID (PAN for 80G donation receipts), and verification documents uploaded exclusively for support requests.
            </div>
            <div className="bg-white p-3.5 rounded-lg border border-amber-200">
              <strong className="text-gray-900 block mb-1">Why we collect it:</strong>
              To process your charitable contributions and issue 80G tax certificates, onboard and coordinate volunteers, evaluate and deliver humanitarian aid, respond to your inquiries, and send optional newsletter updates.
            </div>
            <div className="bg-white p-3.5 rounded-lg border border-amber-200">
              <strong className="text-gray-900 block mb-1">What we never do:</strong>
              We <strong>do not sell</strong>, rent, or monetize your personal data. We do not engage in behavioral profiling, tracking across third-party websites, or unauthorized cross-border transfers.
            </div>
            <div className="bg-white p-3.5 rounded-lg border border-amber-200">
              <strong className="text-gray-900 block mb-1">Your statutory control:</strong>
              Under the DPDP Act 2023, you hold enforceable rights to access (§11), correct (§12), erase (§12) your personal data, withdraw consent with equal ease (§6(4)), designate a nominee (§14), receive grievance resolution within statutory SLAs (Rule 14), and escalate to the Data Protection Board of India (Rule 19).
            </div>
          </div>
        </section>

        {/* Section 2: Data Fiduciary Information */}
        <section className="bg-white rounded-xl shadow-sm p-6 sm:p-8 mb-8 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center mr-3 text-sm font-bold">2</span>
            Data Fiduciary Identity
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-4">
            Under Section 2(i) of the DPDP Act, 2023, the <strong>Data Fiduciary</strong> determining the purpose and means of processing your personal data is:
          </p>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-sm text-gray-700 space-y-1">
            <p><strong>Entity Name:</strong> Lakshmi Venkatesan Charitable Trust (LV Trust)</p>
            <p><strong>Registered Address:</strong> 58, 59 F-3 DJ Garden Extension, Ramamurthy Road, Selvapuram, Coimbatore-641026, Tamil Nadu, India</p>
            <p><strong>Email:</strong> <a href="mailto:hello@mylvtrust.com" className="text-primary hover:underline">hello@mylvtrust.com</a></p>
            <p><strong>Phone:</strong> +91 9942019000</p>
          </div>
        </section>

        {/* Section 3: Categories of Personal Data Collected (PNA-03) */}
        <section className="bg-white rounded-xl shadow-sm p-6 sm:p-8 mb-8 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center mr-3 text-sm font-bold">3</span>
            Categories of Personal Data Collected (§5(1))
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-4">
            We only collect personal data that is adequate, relevant, and strictly necessary for the charitable purposes stated below:
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Category</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Specific Data Elements</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Collection Point</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-gray-600">
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Personal &amp; Contact Details</td>
                  <td className="px-4 py-3">Full name, email address, mobile number, WhatsApp number, postal address, city, state, pincode.</td>
                  <td className="px-4 py-3">Donation, Volunteer, Contact, and Support Request forms.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Demographic Data</td>
                  <td className="px-4 py-3">Age, date of birth, gender.</td>
                  <td className="px-4 py-3">Support Request &amp; Volunteer forms.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Statutory Financial / Tax Information</td>
                  <td className="px-4 py-3">Permanent Account Number (PAN) and donation amount.</td>
                  <td className="px-4 py-3">Donation Form (mandatory under Section 80G of Indian Income Tax Act).</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Verification &amp; Beneficiary Proofs</td>
                  <td className="px-4 py-3">Uploaded documentation: government ID proof, medical reports/bills, tuition fee receipts, or income certificates.</td>
                  <td className="px-4 py-3">Support Request Form (exclusively to evaluate charitable aid eligibility).</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Volunteer Qualification Data</td>
                  <td className="px-4 py-3">Skills, educational background, availability preferences, and motivation statement.</td>
                  <td className="px-4 py-3">Volunteer Registration Form.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Technical &amp; Session Data</td>
                  <td className="px-4 py-3">IP address, browser type, device information, and strictly necessary session cookies.</td>
                  <td className="px-4 py-3">Automated web logs &amp; security infrastructure.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 4: Specific Processing Purposes (PNA-04) */}
        <section className="bg-white rounded-xl shadow-sm p-6 sm:p-8 mb-8 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center mr-3 text-sm font-bold">4</span>
            Specific Purposes of Processing (§5(1)(a))
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-4">
            Under Section 5 of the DPDP Act 2023, data must only be processed for specified, lawful purposes for which the Data Principal has given consent or where processing is permissible under applicable law:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
            <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-1 flex items-center">
                <span className="text-primary mr-2">✓</span> Donation Processing &amp; 80G Tax Exemption
              </h3>
              <p className="text-xs text-gray-600">
                To accept charitable contributions, generate statutory tax exemption certificates under Section 80G of the Income Tax Act, and fulfill mandatory statutory audit disclosures.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-1 flex items-center">
                <span className="text-primary mr-2">✓</span> Beneficiary Assistance &amp; Aid Delivery
              </h3>
              <p className="text-xs text-gray-600">
                To verify genuine hardship, evaluate requests for educational, healthcare, and nutrition support, and distribute trust funds directly to designated beneficiaries.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-1 flex items-center">
                <span className="text-primary mr-2">✓</span> Volunteer Coordination &amp; Engagement
              </h3>
              <p className="text-xs text-gray-600">
                To review volunteer applications, match volunteer skills with charitable field programs, schedule activities, and coordinate emergency relief operations.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-1 flex items-center">
                <span className="text-primary mr-2">✓</span> Inquiries &amp; Community Communications
              </h3>
              <p className="text-xs text-gray-600">
                To reply to queries submitted via our contact channels and provide transparent organizational updates to donors and beneficiaries.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-1 flex items-center">
                <span className="text-primary mr-2">✓</span> Periodic Newsletter &amp; Annual Reports
              </h3>
              <p className="text-xs text-gray-600">
                To share organizational impact reports, upcoming charitable drives, and events with individuals who have explicitly opted into our newsletter subscription.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-1 flex items-center">
                <span className="text-primary mr-2">✓</span> Security &amp; Fraud Prevention
              </h3>
              <p className="text-xs text-gray-600">
                To maintain website availability, authenticate form submissions, safeguard against malicious cyber activities, and protect vulnerable data subjects.
              </p>
            </div>
          </div>
        </section>

        {/* Section 5: Data Retention & Deletion Criteria (PNA-05) */}
        <section className="bg-white rounded-xl shadow-sm p-6 sm:p-8 mb-8 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center mr-3 text-sm font-bold">5</span>
            Data Retention Periods &amp; Erasure Criteria (§8(7))
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-4">
            Under Section 8(7) of the DPDP Act 2023, personal data is retained only for as long as necessary to fulfill the specified purpose for which it was processed, or as mandated by applicable statutory provisions:
          </p>
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Data Type / Process</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Retention Period</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Statutory / Business Rationale</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-gray-600">
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Donation &amp; PAN Records</td>
                  <td className="px-4 py-3">7–8 years from close of financial year</td>
                  <td className="px-4 py-3">Mandatory statutory retention under Indian Income Tax Act, 1961 for Section 80G audit.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Beneficiary Support Records</td>
                  <td className="px-4 py-3">Duration of aid review + 3 years</td>
                  <td className="px-4 py-3">Trust audit compliance and beneficiary impact verification.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Volunteer Records</td>
                  <td className="px-4 py-3">Active tenure + 12 months</td>
                  <td className="px-4 py-3">Re-engagement, program reference, and volunteer certification.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Inquiry &amp; Contact Submissions</td>
                  <td className="px-4 py-3">12 months following query resolution</td>
                  <td className="px-4 py-3">Service quality assurance and follow-up support.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Newsletter Subscriptions</td>
                  <td className="px-4 py-3">Until consent withdrawal</td>
                  <td className="px-4 py-3">Immediate deletion or suppression upon unsubscribe request.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-xs sm:text-sm text-gray-700">
            <strong className="text-gray-900 block mb-1">Deletion &amp; Sanitization Standard:</strong>
            Upon expiration of the retention schedule or valid receipt of a withdrawal/erasure request (where retention is not legally required), files and digital records are permanently wiped using secure electronic sanitization methods or irreversibly anonymized.
          </div>
        </section>

        {/* Section 6: Your Rights as a Data Principal (§11, §12, §14) */}
        <section id="your-rights" className="bg-white rounded-xl shadow-sm p-6 sm:p-8 mb-8 border border-gray-200 scroll-mt-28">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center mr-3 text-sm font-bold">6</span>
              Your Rights as a Data Principal (§11, §12, §14)
            </h2>
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
              Statutory Enforceable Rights
            </span>
          </div>
          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            Under Chapter III of India&apos;s <strong>Digital Personal Data Protection Act, 2023 (DPDP Act)</strong> and the rules framed thereunder, you (as a Data Principal) have absolute, enforceable rights regarding your digital personal data processed by Lakshmi Venkatesan Charitable Trust (Data Fiduciary). These rights are exercisable free of charge:
          </p>

          <div className="space-y-6 text-sm">
            {/* 1. Right to Access Information (§11) */}
            <div className="border border-gray-200 rounded-xl p-5 bg-gray-50/70 hover:bg-white transition-all shadow-sm">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-bold text-gray-900 text-base flex items-center">
                  <span className="text-primary mr-2">1.</span>
                  Right to Access Information (§11)
                </h3>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200">Section 11</span>
              </div>
              <p className="text-gray-700 text-xs sm:text-sm leading-relaxed mb-3">
                Under Section 11 of the DPDP Act 2023, you have the statutory right to obtain from us, upon submitting a verifiable request:
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-xs sm:text-sm text-gray-600 pl-2 mb-3">
                <li><strong>Summary of Personal Data:</strong> A clear, intelligible summary of all digital personal data relating to you that is currently being processed by the Trust.</li>
                <li><strong>Processing Activities Undertaken:</strong> A summary of the specific processing activities undertaken with respect to your personal data across our operations.</li>
                <li><strong>Identities of Processors &amp; Third Parties Shared With:</strong> The identities of all other Data Fiduciaries and Data Processors with whom your personal data has been shared by us, along with a description of the categories of personal data so shared (e.g. payment gateway aggregators for donation processing, authorized cloud infrastructure).</li>
                <li><strong>Prescribed Information:</strong> Any other information related to your personal data and its processing as prescribed by Central Government rules under the Act.</li>
              </ul>
              <div className="bg-white p-3.5 rounded-lg border border-gray-200 text-xs text-gray-700">
                <strong className="text-gray-900 block mb-1">Practical Mechanism &amp; Submission:</strong>
                To exercise your Right to Access, send an email to our Grievance Redressal Officer at <a href="mailto:hello@mylvtrust.com" className="text-primary font-medium hover:underline">hello@mylvtrust.com</a> or <a href="mailto:grievance@bcbuzz.tech" className="text-primary font-medium hover:underline">grievance@bcbuzz.tech</a> with the subject line <em>&quot;DPDPA Right to Access Request&quot;</em> along with basic proof of identity. We will verify your request and provide a comprehensive access summary free of charge within our statutory resolution SLA.
              </div>
            </div>

            {/* 2. Right to Correction and Updating (§12(1)) */}
            <div className="border border-gray-200 rounded-xl p-5 bg-gray-50/70 hover:bg-white transition-all shadow-sm">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-bold text-gray-900 text-base flex items-center">
                  <span className="text-primary mr-2">2.</span>
                  Right to Correction and Updating (§12(1))
                </h3>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200">Section 12(1)</span>
              </div>
              <p className="text-gray-700 text-xs sm:text-sm leading-relaxed mb-3">
                Under Section 12(1) of the DPDP Act 2023, you have the right to request:
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-xs sm:text-sm text-gray-600 pl-2 mb-3">
                <li><strong>Correction of Inaccurate or Misleading Data:</strong> Rectification of any erroneous, inaccurate, or misleading personal data maintained in the Trust&apos;s records.</li>
                <li><strong>Completion of Incomplete Records:</strong> Completion of any incomplete personal data in your volunteer profile, donor record, or support request submission.</li>
                <li><strong>Updating Outdated Data:</strong> Updating of changed contact numbers, postal addresses, or legal identifiers to maintain accurate records.</li>
              </ul>
              <div className="bg-white p-3.5 rounded-lg border border-gray-200 text-xs text-gray-700">
                <strong className="text-gray-900 block mb-1">Practical Mechanism &amp; Submission:</strong>
                Email our Grievance Officer with the subject line <em>&quot;DPDPA Correction Request&quot;</em>, stating the exact data field to be amended, the corrected information, and supporting documentation where applicable (e.g., updated address proof or PAN card copy). We will update your record across all active databases and notify relevant processors.
              </div>
            </div>

            {/* 3. Right to Erasure (§12(2)–(3)) */}
            <div className="border border-gray-200 rounded-xl p-5 bg-gray-50/70 hover:bg-white transition-all shadow-sm">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-bold text-gray-900 text-base flex items-center">
                  <span className="text-primary mr-2">3.</span>
                  Right to Erasure (§12(2)–(3))
                </h3>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200">Section 12(2)</span>
              </div>
              <p className="text-gray-700 text-xs sm:text-sm leading-relaxed mb-3">
                Under Section 12(2) of the DPDP Act 2023, you have the right to request the permanent erasure/deletion of your personal data when:
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-xs sm:text-sm text-gray-600 pl-2 mb-3">
                <li>The specified purpose for which the personal data was collected has been accomplished and retention is no longer necessary.</li>
                <li>You have withdrawn your consent where processing was based on consent.</li>
                <li>Retention is no longer lawful under the Act.</li>
              </ul>
              <div className="bg-amber-50 p-3.5 rounded-lg border border-amber-200 text-xs text-amber-900 mb-3 leading-relaxed">
                <strong>Statutory Exceptions (§12(3)):</strong> Under Section 12(3) of the Act, erasure shall not apply where retention of personal data is required for compliance with any law for the time being in force. Specifically, statutory donor records, receipt numbers, and PAN details must be preserved for 7–8 years under Section 80G of the Indian Income Tax Act, 1961 and statutory trust audit regulations.
              </div>
              <div className="bg-white p-3.5 rounded-lg border border-gray-200 text-xs text-gray-700">
                <strong className="text-gray-900 block mb-1">Practical Mechanism &amp; Submission:</strong>
                Send an erasure request to <a href="mailto:hello@mylvtrust.com" className="text-primary font-medium hover:underline">hello@mylvtrust.com</a> or <a href="mailto:grievance@bcbuzz.tech" className="text-primary font-medium hover:underline">grievance@bcbuzz.tech</a>. Upon receipt, all non-exempt personal data will be permanently wiped using secure electronic sanitization protocols or irreversibly anonymized.
              </div>
            </div>

            {/* 4. Right to Withdraw Consent (§6(4)) */}
            <div className="border border-gray-200 rounded-xl p-5 bg-gray-50/70 hover:bg-white transition-all shadow-sm">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-bold text-gray-900 text-base flex items-center">
                  <span className="text-primary mr-2">4.</span>
                  Right to Withdraw Consent (§6(4) - Symmetrical Withdrawal)
                </h3>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200">Section 6(4)</span>
              </div>
              <p className="text-gray-700 text-xs sm:text-sm leading-relaxed mb-3">
                Section 6(4) of the DPDP Act 2023 mandates that where processing of personal data is based on consent, you have the statutory right to withdraw your consent at any time, with the <strong>ease of such withdrawal being comparable to the ease with which such consent was given</strong>.
              </p>
              <div className="bg-white p-4 rounded-lg border border-gray-200 text-xs sm:text-sm text-gray-700 space-y-2.5 mb-3">
                <p className="font-semibold text-gray-900">Direct, Symmetrical Withdrawal Channels Available on Every Page:</p>
                <div className="flex flex-wrap gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleWithdrawConsent}
                    className="inline-flex items-center px-3.5 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium text-xs shadow-sm transition-all active:scale-95"
                    title="Withdraw all optional consent as easily as it was given (§6(4))"
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    1-Click Instant Consent Withdrawal
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenPreferences}
                    className="inline-flex items-center px-3.5 py-2 rounded-lg bg-secondary hover:bg-secondary-dark text-white font-medium text-xs shadow-sm transition-all active:scale-95"
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    Manage Privacy Preferences
                  </button>
                </div>
                <div className="text-xs text-gray-600 pt-1 space-y-1">
                  <p>• <strong>Persistent Global Footer:</strong> Every page on our website includes persistent &quot;Withdraw Consent&quot; and &quot;Manage Privacy Preferences&quot; options.</p>
                  <p>• <strong>Floating Privacy Badge:</strong> A shield icon at the bottom-left corner allows you to review or revoke consent anytime.</p>
                  <p>• <strong>Direct Written Notice:</strong> Email your withdrawal statement directly to our Grievance Officer at <a href="mailto:hello@mylvtrust.com" className="text-primary hover:underline">hello@mylvtrust.com</a>.</p>
                </div>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                <strong>Legal Consequences of Withdrawal:</strong> Withdrawal of consent does not affect the legality of any personal data processing carried out prior to such withdrawal, nor does it affect processing necessary for compliance with legal obligations or processing permissible under Section 7 of the Act.
              </p>
            </div>

            {/* 5. Right of Nomination & Nomination Mechanism (§14) */}
            <div className="border-2 border-emerald-200 rounded-xl p-5 bg-emerald-50/50 hover:bg-emerald-50/80 transition-all shadow-sm">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-bold text-emerald-950 text-base flex items-center">
                  <span className="text-emerald-700 mr-2">5.</span>
                  Right of Nomination &amp; Nomination Mechanism (§14)
                </h3>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-emerald-200 text-emerald-900 border border-emerald-300">Section 14</span>
              </div>
              <p className="text-emerald-900 text-xs sm:text-sm leading-relaxed mb-4">
                Under Section 14 of the Digital Personal Data Protection Act, 2023, you have the statutory right to nominate any individual who shall, in the event of your <strong>death</strong> or <strong>incapacity</strong> (defined by the Act as unsoundness of mind or physical infirmity), exercise all Data Principal rights on your behalf.
              </p>

              <div className="bg-white p-4 rounded-lg border border-emerald-200 text-xs sm:text-sm text-gray-800 space-y-3">
                <h4 className="font-bold text-emerald-900 text-sm">Nomination Mechanism &amp; Operating Procedure:</h4>
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  <li>
                    <strong>How to Register a Nomination (Free of Charge):</strong>
                    <div className="pl-5 mt-1 text-xs text-gray-600 space-y-1">
                      The Data Principal may designate a nominee at any time free of charge by sending an email or signed letter to the Grievance Redressal Officer at <a href="mailto:hello@mylvtrust.com" className="text-primary font-medium hover:underline">hello@mylvtrust.com</a> or <a href="mailto:grievance@bcbuzz.tech" className="text-primary font-medium hover:underline">grievance@bcbuzz.tech</a> with the subject line <em>&quot;DPDPA Nomination Registration&quot;</em>. The communication must contain:
                      <ul className="list-disc list-inside pl-3 pt-1 space-y-0.5">
                        <li>Data Principal&apos;s full legal name, registered mobile number, and email address.</li>
                        <li>Nominee&apos;s full legal name, relationship to the Data Principal, date of birth, contact email, telephone number, and postal address.</li>
                        <li>A copy of government-issued photo identification for the nominee (e.g., Aadhaar card, Voter ID, or Passport).</li>
                      </ul>
                    </div>
                  </li>
                  <li>
                    <strong>Modification &amp; Revocation:</strong>
                    <div className="pl-5 mt-1 text-xs text-gray-600">
                      The Data Principal retains the right to modify, replace, or revoke a registered nomination at any time during their lifetime with immediate effect by notifying the Grievance Officer in writing.
                    </div>
                  </li>
                  <li>
                    <strong>How a Nominee Exercises Rights:</strong>
                    <div className="pl-5 mt-1 text-xs text-gray-600 space-y-1">
                      In the event of the Data Principal&apos;s death or incapacity, the designated nominee may submit a rights request (Access, Correction, Erasure, Consent Withdrawal, or Grievance Redressal) accompanied by:
                      <ul className="list-disc list-inside pl-3 pt-1 space-y-0.5">
                        <li>In case of death: An official Death Certificate issued by the competent municipal or government registrar.</li>
                        <li>In case of incapacity: A Medical Incapacity Certificate issued by a registered medical practitioner establishing physical infirmity or unsoundness of mind.</li>
                        <li>The nominee&apos;s verified government photo identity document.</li>
                      </ul>
                      Following verification, the nominee will be formally recognized to exercise all statutory Data Principal rights.
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>

          {/* Practical Mechanism Box */}
          <div className="mt-6 p-5 rounded-xl bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border border-primary/20 text-xs sm:text-sm text-gray-800">
            <h4 className="text-primary font-bold text-sm mb-2 flex items-center">
              <svg className="w-5 h-5 mr-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Practical Channel to Exercise Your Rights (Free of Charge)
            </h4>
            <p className="text-gray-700 leading-relaxed mb-3">
              You or your lawful nominee may submit any statutory rights request without paying any fee. Please direct your request to our designated official channel:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-white p-3.5 rounded-lg border border-primary/20 text-xs">
              <div>
                <span className="text-gray-500 block">Primary Email:</span>
                <a href="mailto:hello@mylvtrust.com" className="text-primary font-bold hover:underline">hello@mylvtrust.com</a>
              </div>
              <div>
                <span className="text-gray-500 block">Grievance Desk:</span>
                <a href="mailto:grievance@bcbuzz.tech" className="text-primary font-bold hover:underline">grievance@bcbuzz.tech</a>
              </div>
              <div>
                <span className="text-gray-500 block">Helpline / Phone:</span>
                <strong className="text-gray-900">+91 9942019000</strong>
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              <strong>Statutory Turnaround:</strong> Acknowledged within <strong>48 hours</strong>; complete resolution within <strong>30 calendar days</strong>, strictly not exceeding <strong>90 days</strong> pursuant to Rule 14.
            </p>
          </div>
        </section>

        {/* Section 7: Grievance Redressal Officer & Statutory SLA (Rule 14) */}
        <section id="grievance-redressal" className="bg-white rounded-xl shadow-sm p-6 sm:p-8 mb-8 border border-gray-200 scroll-mt-28">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center mr-3 text-sm font-bold">7</span>
              Grievance Redressal Officer &amp; Statutory SLA (Rule 14)
            </h2>
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
              §5(1)(ii), §8(9) &amp; Rule 14
            </span>
          </div>
          <p className="text-gray-700 text-sm leading-relaxed mb-4">
            In compliance with Section 5(1)(ii) and Section 8(9) of the Digital Personal Data Protection Act, 2023 and <strong>Rule 14 of the Digital Personal Data Protection Rules</strong>, Lakshmi Venkatesan Charitable Trust has established an effective, accessible grievance redressal mechanism and appointed a designated Grievance Redressal Officer:
          </p>

          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 text-sm text-gray-800 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <span className="text-gray-500 text-xs block">Name of Grievance Redressal Officer:</span>
                <strong className="text-gray-900 text-base">MAHARAJ MANOHARAN</strong>
              </div>
              <div>
                <span className="text-gray-500 text-xs block">Designation:</span>
                <span className="text-gray-900 font-medium">Designated Grievance Redressal Officer (DPDPA)</span>
              </div>
              <div>
                <span className="text-gray-500 text-xs block">Contact Email Addresses:</span>
                <a href="mailto:hello@mylvtrust.com" className="text-primary font-medium hover:underline">hello@mylvtrust.com</a>
                <span className="text-gray-400 text-xs mx-2">•</span>
                <a href="mailto:grievance@bcbuzz.tech" className="text-primary font-medium hover:underline">grievance@bcbuzz.tech</a>
              </div>
              <div>
                <span className="text-gray-500 text-xs block">Direct Contact Telephone:</span>
                <span className="text-gray-900 font-medium">+91 9942019000</span>
              </div>
            </div>

            <div className="pt-3 border-t border-gray-200 text-xs text-gray-600">
              <span className="font-semibold text-gray-700">Official Postal Address:</span> 58, 59 F-3 DJ Garden Extension, Ramamurthy Road, Selvapuram, Coimbatore-641026, Tamil Nadu, India.
            </div>

            {/* Statutory SLA Box (Rule 14) */}
            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-xs sm:text-sm text-emerald-950 space-y-2">
              <div className="flex items-center space-x-2 font-bold text-emerald-900">
                <svg className="w-5 h-5 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Statutory Resolution Service Standards &amp; SLA (Rule 14):</span>
              </div>
              <ul className="list-disc list-inside space-y-1 text-emerald-900 text-xs sm:text-sm pl-2">
                <li><strong>Acknowledgment of Grievance / Rights Request:</strong> Within <strong>48 hours</strong> of receipt.</li>
                <li><strong>Trust Operational Resolution Target:</strong> Thorough inquiry and formal resolution within <strong>30 calendar days</strong>.</li>
                <li><strong>Statutory Maximum Ceiling:</strong> Pursuant to <strong>Rule 14 of the Digital Personal Data Protection Rules</strong>, the resolution timeframe for any grievance or rights request <strong>shall not exceed ninety (90) days</strong> from the date of receipt.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 8: Escalation to Data Protection Board of India (§13(3) & Rule 19) */}
        <section id="dpbi-escalation" className="bg-white rounded-xl shadow-sm p-6 sm:p-8 mb-8 border border-gray-200 scroll-mt-28">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center mr-3 text-sm font-bold">8</span>
              Escalation to the Data Protection Board of India for Rights Requests &amp; Grievances (§13(3) &amp; Rule 19)
            </h2>
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
              Statutory Regulatory Redressal
            </span>
          </div>

          <div className="p-5 rounded-xl bg-blue-50/70 border border-blue-200 text-sm text-gray-700 leading-relaxed mb-4 space-y-3">
            <p>
              In accordance with <strong>Section 13(3) of the Digital Personal Data Protection Act, 2023</strong> and <strong>Rule 19 of the Digital Personal Data Protection Rules</strong>, if you have submitted a <strong>statutory rights request</strong> (Right to Access §11, Right to Correction/Updating §12, Right to Erasure §12, Right to Withdraw Consent §6(4), or Right of Nomination §14) or filed a <strong>grievance</strong> with Lakshmi Venkatesan Charitable Trust, and:
            </p>
            <ul className="list-disc list-inside space-y-1 pl-2 text-xs sm:text-sm text-gray-800 font-medium">
              <li>Our Grievance Redressal Officer fails to respond or resolve your request within the statutory timeframe (not exceeding 90 days under Rule 14), OR</li>
              <li>You are unsatisfied with the resolution or order communicated by the Trust, OR</li>
              <li>You reasonably believe there has been a breach or non-compliance under the Act.</li>
            </ul>
            <p className="text-xs sm:text-sm text-gray-700">
              You or your designated nominee have the statutory right to escalate your complaint directly to the <strong>Data Protection Board of India (DPBI)</strong>.
            </p>

            {/* Official Board Information Box */}
            <div className="bg-white p-4 rounded-xl border border-blue-200 text-xs sm:text-sm space-y-2 text-gray-800">
              <h4 className="font-bold text-blue-900 text-sm">Verified Official Information for DPBI Escalation:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                <div>
                  <span className="text-gray-500 text-xs block">Regulatory Authority:</span>
                  <strong className="text-gray-900">Data Protection Board of India (DPBI)</strong>
                </div>
                <div>
                  <span className="text-gray-500 text-xs block">Statutory Basis:</span>
                  <span className="text-gray-900 font-medium">Constituted under Section 18 of DPDP Act, 2023</span>
                </div>
                <div>
                  <span className="text-gray-500 text-xs block">Official Portal / Website:</span>
                  <a
                    href="https://www.dpbi.gov.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 underline font-bold hover:text-blue-900"
                  >
                    www.dpbi.gov.in
                  </a>
                </div>
                <div>
                  <span className="text-gray-500 text-xs block">Applicable Rules:</span>
                  <span className="text-gray-900 font-medium">Rule 19 (Complaints to the Board)</span>
                </div>
              </div>

              <div className="pt-2 border-t border-gray-100 text-xs text-gray-600 space-y-1">
                <p><strong>Filing Procedure:</strong></p>
                <ol className="list-decimal list-inside space-y-1 pl-1">
                  <li>Ensure you have first submitted your rights request or grievance to the Trust&apos;s Grievance Officer and waited for the statutory response period or received an unsatisfactory resolution (§13(2)).</li>
                  <li>Access the official electronic portal at <a href="https://www.dpbi.gov.in" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">www.dpbi.gov.in</a> or designated Government of India digital interfaces.</li>
                  <li>Lodge an electronic complaint under Rule 19, attaching copies of:
                    <ul className="list-disc list-inside pl-4 pt-0.5 space-y-0.5 text-gray-500">
                      <li>The initial rights request or grievance sent to LV Trust.</li>
                      <li>The 48-hour acknowledgment receipt or transmission record.</li>
                      <li>The resolution or decision issued by the Grievance Officer (or evidence of non-response past the 90-day SLA).</li>
                    </ul>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* Section 9: Third-Party Processors & Data Residency */}
        <section className="bg-white rounded-xl shadow-sm p-6 sm:p-8 mb-8 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center mr-3 text-sm font-bold">9</span>
            Third-Party Processors &amp; Cross-Border Transfers (§16)
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-3">
            In compliance with Section 5(1)(b) and Section 16 of the DPDP Act 2023:
          </p>
          <ul className="list-disc list-inside space-y-2 text-sm text-gray-600 mb-4">
            <li><strong>Data Residency:</strong> Primary data storage and processing remain strictly within Indian jurisdiction servers.</li>
            <li><strong>Service Delivery Processors:</strong> We engage verified technological partners strictly for transaction delivery (e.g., Razorpay/authorized payment gateways for donations, encrypted cloud infrastructure in AWS Mumbai region, and transactional email infrastructure).</li>
            <li><strong>Cross-Border Transfers:</strong> We do not transfer personal data to restricted jurisdictions and comply fully with Central Government adequacy notifications.</li>
          </ul>
        </section>

        {/* Section 10: Significant Data Fiduciary (SDF) Applicability Review (§10) */}
        <section id="sdf-applicability" className="bg-white rounded-xl shadow-sm p-6 sm:p-8 mb-8 border border-gray-200 scroll-mt-28">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center mr-3 text-sm font-bold">10</span>
              Significant Data Fiduciary (SDF) Applicability Assessment (§10)
            </h2>
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-300">
              Applicability Review Required
            </span>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-4">
            Under <strong>Section 10 of the Digital Personal Data Protection Act, 2023</strong>, enhanced statutory compliance obligations apply exclusively to entities formally notified by the Central Government of India as <strong>Significant Data Fiduciaries (SDF)</strong>. In accordance with statutory transparency standards, this section documents the legal baseline and current applicability assessment for each specialized SDF control:
          </p>

          {/* Statutory Assessment Factors (§10(1)) */}
          <div className="bg-amber-50/70 border border-amber-200 rounded-xl p-4 sm:p-5 mb-6 text-xs sm:text-sm text-gray-800 space-y-2">
            <h3 className="font-bold text-amber-950 text-sm flex items-center">
              <svg className="w-4 h-4 mr-2 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Statutory Basis for SDF Designation (§10(1)):
            </h3>
            <p className="text-gray-700 leading-relaxed">
              Under Section 10(1) of the DPDP Act 2023, the Central Government designates Significant Data Fiduciaries based on a holistic assessment of:
              <em> (a) volume and sensitivity of personal data processed; (b) risk to rights of Data Principals; (c) impact on the sovereignty and integrity of India; (d) risk to electoral democracy; (e) security of the State; and (f) public order</em>.
            </p>
            <p className="text-gray-600 text-xs">
              <strong>Current Governance Position:</strong> Lakshmi Venkatesan Charitable Trust (LV Trust) operates as a registered non-profit public charitable trust engaged in humanitarian aid, student mentorship, and community welfare. As of the date of this notice, the Central Government has not notified LV Trust as a Significant Data Fiduciary under Section 10(1). Pending official threshold notifications from the Central Government / Ministry of Electronics and Information Technology (MeitY), the following controls are recorded under an active <strong>Applicability Review Required</strong> framework.
            </p>
          </div>

          {/* Detailed Controls: SDF-03, SDF-04, SDF-05 */}
          <div className="space-y-5 text-sm">
            {/* Control 1: SDF-03 DPO Appointment (§10(2)(a)) */}
            <div className="border border-gray-200 rounded-xl p-5 bg-gray-50/60 hover:bg-white transition-all shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                <h3 className="font-bold text-gray-900 text-base flex items-center">
                  <span className="text-primary mr-2">10.1</span>
                  Data Protection Officer (DPO) Appointment (§10(2)(a) / SDF-03)
                </h3>
                <span className="text-xs font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200">
                  Status: Applicability Review Required
                </span>
              </div>
              <div className="text-xs sm:text-sm text-gray-700 space-y-2">
                <p>
                  <strong>Statutory Requirement:</strong> Under Section 10(2)(a), a Significant Data Fiduciary must appoint a dedicated Data Protection Officer (DPO) based in India who reports directly to the Board of Directors or governing body and represents the SDF.
                </p>
                <p>
                  <strong>Operational Baseline &amp; Approved Evidence:</strong> Under Section 8(9) and Section 5(1)(ii) for Data Fiduciaries, LV Trust has designated an official <strong>Grievance Redressal Officer (GRO)</strong>: <strong className="text-gray-900">MAHARAJ MANOHARAN</strong> (<a href="mailto:hello@mylvtrust.com" className="text-primary hover:underline">hello@mylvtrust.com</a> / <a href="mailto:grievance@bcbuzz.tech" className="text-primary hover:underline">grievance@bcbuzz.tech</a>, +91 9942019000), who serves as the designated point of contact for all Data Principal rights and privacy inquiries.
                </p>
                <p>
                  <strong>Applicability Determination:</strong> Because LV Trust has not been notified as a Significant Data Fiduciary under §10(1), a statutory board-reporting DPO under §10(2)(a) is <em>conditionally not applicable</em> under current law, with grievance functions fully fulfilled by the designated Grievance Officer. Full DPO applicability remains subject to formal annual volume review and forthcoming Central Government notification.
                </p>
              </div>
            </div>

            {/* Control 2: SDF-04 DPIA Process (§10(2)(c)) */}
            <div className="border border-gray-200 rounded-xl p-5 bg-gray-50/60 hover:bg-white transition-all shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                <h3 className="font-bold text-gray-900 text-base flex items-center">
                  <span className="text-primary mr-2">10.2</span>
                  Data Protection Impact Assessment (DPIA) Process (§10(2)(c) / SDF-04)
                </h3>
                <span className="text-xs font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200">
                  Status: Applicability Review Required
                </span>
              </div>
              <div className="text-xs sm:text-sm text-gray-700 space-y-2">
                <p>
                  <strong>Statutory Requirement:</strong> Under Section 10(2)(c), a Significant Data Fiduciary must periodically undertake a formal Data Protection Impact Assessment (DPIA) covering description of processing, assessment of harm to rights of Data Principals, and risk mitigation measures.
                </p>
                <p>
                  <strong>Operational Baseline &amp; Approved Evidence:</strong> LV Trust implements continuous data protection safeguards: strict purpose limitation (§5), minimization of personal data collection, role-based access controls for beneficiary hardship verification, electronic sanitization protocols (§8(7)), and SSL/TLS encryption for all website communications. The Trust does not perform automated algorithmic profiling, cross-site behavioral tracking, or high-risk automated decision-making.
                </p>
                <p>
                  <strong>Applicability Determination:</strong> Formal statutory DPIA audits under §10(2)(c) are mandatory solely for notified SDFs. While baseline privacy assessments are implemented organically across charitable programs, a formal statutory DPIA process remains under review pending publication of prescribed DPIA rules and SDF thresholds by the Central Government.
                </p>
              </div>
            </div>

            {/* Control 3: SDF-05 Independent Data Audit Practice (§10(2)(b)) */}
            <div className="border border-gray-200 rounded-xl p-5 bg-gray-50/60 hover:bg-white transition-all shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                <h3 className="font-bold text-gray-900 text-base flex items-center">
                  <span className="text-primary mr-2">10.3</span>
                  Independent Data Audit Practice (§10(2)(b) / SDF-05)
                </h3>
                <span className="text-xs font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200">
                  Status: Applicability Review Required
                </span>
              </div>
              <div className="text-xs sm:text-sm text-gray-700 space-y-2">
                <p>
                  <strong>Statutory Requirement:</strong> Under Section 10(2)(b), a Significant Data Fiduciary must appoint an independent data auditor to carry out periodic data audits evaluating compliance with the provisions of the DPDP Act.
                </p>
                <p>
                  <strong>Operational Baseline &amp; Approved Evidence:</strong> LV Trust undergoes regular statutory financial and governance audits by independent chartered accountants in compliance with the Indian Trusts Act and Income Tax Act, 1961 (for Section 80G charitable status). Consent preferences and digital data handling logs are maintained systematically via on-site consent infrastructure.
                </p>
                <p>
                  <strong>Applicability Determination:</strong> The appointment of an independent data auditor under §10(2)(b) is statutorily compelled exclusively for notified Significant Data Fiduciaries. As LV Trust has not been notified under §10(1), independent third-party data audit is <em>conditionally not applicable</em> at present, with readiness maintained through annual governance reviews by the Board of Trustees.
                </p>
              </div>
            </div>
          </div>

          {/* Applicability Review Governance Ledger / Template */}
          <div className="mt-6 bg-gray-50 border border-gray-300 rounded-xl p-5 text-xs sm:text-sm">
            <h3 className="font-bold text-gray-900 text-sm mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              SDF Applicability Governance Ledger &amp; Review Template
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Governance Parameter</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Current Evaluated Status</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Statutory Citation &amp; Evidence Basis</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-gray-700 bg-white">
                  <tr>
                    <td className="px-3 py-2 font-medium text-gray-900">Entity Classification</td>
                    <td className="px-3 py-2">Standard Data Fiduciary</td>
                    <td className="px-3 py-2">Section 2(i), DPDP Act, 2023 (Charitable Trust)</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-medium text-gray-900">Government SDF Notification (§10(1))</td>
                    <td className="px-3 py-2">Not Notified</td>
                    <td className="px-3 py-2">No Central Government notification issued to LV Trust to date</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-medium text-gray-900">DPO Appointment (§10(2)(a) / SDF-03)</td>
                    <td className="px-3 py-2"><span className="text-amber-800 font-semibold bg-amber-100 px-2 py-0.5 rounded">Review Required</span></td>
                    <td className="px-3 py-2">Conditionally Not Applicable; Grievance Officer active under §8(9)</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-medium text-gray-900">DPIA Process (§10(2)(c) / SDF-04)</td>
                    <td className="px-3 py-2"><span className="text-amber-800 font-semibold bg-amber-100 px-2 py-0.5 rounded">Review Required</span></td>
                    <td className="px-3 py-2">Conditionally Not Applicable; Baseline privacy safeguards active</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-medium text-gray-900">Independent Data Audit (§10(2)(b) / SDF-05)</td>
                    <td className="px-3 py-2"><span className="text-amber-800 font-semibold bg-amber-100 px-2 py-0.5 rounded">Review Required</span></td>
                    <td className="px-3 py-2">Conditionally Not Applicable; Trust statutory audits active</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-medium text-gray-900">Responsible Governance Authority</td>
                    <td className="px-3 py-2">Board of Trustees &amp; GRO</td>
                    <td className="px-3 py-2">Lakshmi Venkatesan Charitable Trust Governance Body</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-medium text-gray-900">Review Frequency</td>
                    <td className="px-3 py-2">Annual / Upon MeitY Rule Release</td>
                    <td className="px-3 py-2">Continuous regulatory tracking for Section 10 notifications</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-[11px] text-gray-500 italic">
              Note: This governance statement does not constitute a definitive legal determination or claim of statutory exemption. Formal applicability is re-evaluated periodically in consultation with legal counsel as DPDPA rules, threshold notifications, and guidelines are notified by the Central Government.
            </p>
          </div>
        </section>

        {/* Section 11: Notice Governance & Version History (PNA-12) */}
        <section className="bg-white rounded-xl shadow-sm p-6 sm:p-8 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center mr-3 text-sm font-bold">11</span>
            Notice Governance &amp; Version History
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-4">
            This notice is maintained continuously in an easily accessible form on our website and at every digital point of personal data collection in compliance with DPDPA 2023 §5 and Rule 3:
          </p>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-xs sm:text-sm text-gray-600 space-y-1">
            <p><strong>Current Active Version:</strong> Version 2.1 (DPDPA 2023 §§5, 6, 8, 10, 11, 12, 14 &amp; Rules 3, 14, 19 Compliant)</p>
            <p><strong>Effective Date:</strong> September 29, 2026</p>
            <p><strong>Previous Versions:</strong> Version 2.0 (Statutory Rights &amp; SLA Update), Version 1.0 (Initial Compliance Notice)</p>
            <p><strong>Compliance Ledger Notice ID:</strong> <code className="bg-white px-2 py-0.5 rounded border border-gray-300 font-mono text-gray-800">6439c25a-00d9-416a-b622-16bdfb4730ef</code></p>
            <p><strong>Supported Notice Languages:</strong> English, Hindi, and Tamil.</p>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-gray-200">
            <Link
              to="/"
              className="inline-flex items-center text-primary font-semibold text-sm hover:underline"
            >
              ← Return to LV Trust Home
            </Link>
            <div className="flex items-center space-x-4 text-xs">
              <button
                type="button"
                onClick={handleWithdrawConsent}
                className="text-red-600 hover:text-red-800 font-semibold underline transition-colors"
                title="Withdraw all optional consent as easily as it was given (§6(4))"
              >
                Withdraw Consent (§6(4))
              </button>
              <button
                type="button"
                onClick={handleOpenPreferences}
                className="text-gray-500 hover:text-gray-900 underline transition-colors"
              >
                Manage Cookies &amp; Consent Preferences
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
